import { MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import ApiProvider from 'api/api-provider';
import React from 'react';
import { DepartmentTerminologyProvider } from 'model/DepartmentTerminologyProvider';
import { RouterProvider } from 'react-router-dom';
import { Notifications } from '@mantine/notifications';
import { router } from 'routes/AppRoutes';
import theme from 'theme';
import FileManagerProvider from 'api/file-manager/file-manager-provider';
import { PickDeviceTypeProvider } from 'components/modals/pick-device-type/PickDeviceTypeProvider';
import MessageProvider from 'components/modals/message/MessageProvider';
import { AddDeviceFileProvider } from 'components/modals/add-device-file/AddDeviceFileProvider';
import { PickUsersProvider } from 'components/modals/pick-users-modal/PickUsersProvider';
import { PermissionDataProvider } from 'model/PermissionDataProvider';
import { FixDeviceFaultProvider } from 'components/modals/fix-device-fault/FixDeviceFaultProvider';
import ConfirmWithMessageProvider from 'components/modals/confirm-with-message/ConfirmWithMessageProvider';
import DelegateProvider from 'components/modals/delegate/DelegateProvider';
import { PickOrganizationsProvider } from 'components/modals/pick-organizations-modal/PickOrganizationsProvider';
import { PickDepartmentsProvider } from 'components/modals/pick-departments-modal/PickDepartmentsProvider';
import { PickDevicesProvider } from 'components/modals/pick-devices-modal/PickDevicesProvider';
import { PickGlobalPermissionsProvider } from 'components/modals/pick-global-permissions-modal/PickGlobalPermissionsProvider';
import BuildVersionProvider from 'components/BuildVersionProvider';

// Firebase
import { initializeApp } from 'firebase/app';
import { getPerformance } from 'firebase/performance';
import {
  FEATURE_TOGGLE_FIREBASE,
  FIREBASE_API_KEY,
  FIREBASE_APP_ID,
  FIREBASE_AUTH_DOMAIN,
  FIREBASE_MESSAGING_SENDER_ID,
  FIREBASE_PROJECT_ID,
  FIREBASE_STORAGE_BUCKET,
} from 'env';
import SignRevisionModalProvider from 'components/modals/sign-revision-modal/SignRevisionModalProvider';
import BulkSignRevisionModalProvider from 'components/modals/bulk-sign-revision-modal/BulkSignRevisionModalProvider';
import FinishSigningModalProvider from 'components/modals/finish-signing-modal/FinishSigningModalProvider';
import { GatherConfirmationProvider } from 'components/tables/data/revision/actions/confirm/GatherConfirmationProvider';
import { ConfirmStepProvider } from 'components/tables/data/revision/actions/confirm/ConfirmStepProvider';
import { TableProfilesModalProvider } from 'components/modals/table-profiles-modal/TableProfilesModalProvider';
import { SyncRemoteDeviceModalProvider } from 'components/modals/sync-remote-device-modal/SyncRemoteDeviceProvider';
import CopyRemoteSourceConfigProvider from 'components/modals/copy-remote-source-config/CopyRemoteSourceConfigProvider';
import ImportXlsxModalProvider from 'components/modals/import-xlsx-modal/ImportXlsxModalProvider';
import { ResponsiveDesignProvider } from 'hooks/use-responsive-design';
import { PasswordValidityModalProvider } from 'components/modals/password-validity-modal/PasswordValidityModalProvider';
import { ChangeDeviceRemoteSourceModalProvider } from 'components/modals/change-device-remote-source/ChangeDeviceRemoteSourceModalProvider';
import { CreateRevisionPredefinedDataModalProvider } from 'components/modals/create-revision-predefined-data-modal/CreateRevisionPredefinedDataModalProvider';
import { DeviceQrPairingModalProvider } from 'components/modals/device-qr-pairing/DeviceQrPairingModalProvider';

if (FEATURE_TOGGLE_FIREBASE) {
  const firebaseConfig = {
    apiKey: FIREBASE_API_KEY,
    authDomain: FIREBASE_AUTH_DOMAIN,
    projectId: FIREBASE_PROJECT_ID,
    storageBucket: FIREBASE_STORAGE_BUCKET,
    messagingSenderId: FIREBASE_MESSAGING_SENDER_ID,
    appId: FIREBASE_APP_ID,
  };

  // Initialize Firebase
  const firebase = initializeApp(firebaseConfig);
  getPerformance(firebase);
}

/**
 * The entry point of the application.
 */
export default function App() {
  return (
    <React.StrictMode>
      <MantineProvider withGlobalStyles withNormalizeCSS withCSSVariables theme={theme}>
        <Notifications autoClose={8000} />
        <ResponsiveDesignProvider>
          <ApiProvider>
            <DepartmentTerminologyProvider>
              <FileManagerProvider>
                <ModalsProvider>
                  <MessageProvider>
                    <ConfirmWithMessageProvider>
                      <PickDeviceTypeProvider>
                        <DelegateProvider>
                          <SignRevisionModalProvider>
                            <FinishSigningModalProvider>
                              <BulkSignRevisionModalProvider>
                                <PermissionDataProvider>
                                  <PickUsersProvider>
                                    <PickGlobalPermissionsProvider>
                                      <PickOrganizationsProvider>
                                        <PickDepartmentsProvider>
                                          <PickDevicesProvider>
                                            <BuildVersionProvider>
                                              <AddDeviceFileProvider>
                                                <FixDeviceFaultProvider>
                                                  <GatherConfirmationProvider>
                                                    <ConfirmStepProvider>
                                                      <TableProfilesModalProvider>
                                                        <SyncRemoteDeviceModalProvider>
                                                          <CreateRevisionPredefinedDataModalProvider>
                                                            <ImportXlsxModalProvider>
                                                              <PasswordValidityModalProvider>
                                                                <ChangeDeviceRemoteSourceModalProvider>
                                                                  <DeviceQrPairingModalProvider>
                                                                    <CopyRemoteSourceConfigProvider>
                                                                      <RouterProvider router={router} />
                                                                    </CopyRemoteSourceConfigProvider>
                                                                  </DeviceQrPairingModalProvider>
                                                                </ChangeDeviceRemoteSourceModalProvider>
                                                              </PasswordValidityModalProvider>
                                                            </ImportXlsxModalProvider>
                                                          </CreateRevisionPredefinedDataModalProvider>
                                                        </SyncRemoteDeviceModalProvider>
                                                      </TableProfilesModalProvider>
                                                    </ConfirmStepProvider>
                                                  </GatherConfirmationProvider>
                                                </FixDeviceFaultProvider>
                                              </AddDeviceFileProvider>
                                            </BuildVersionProvider>
                                          </PickDevicesProvider>
                                        </PickDepartmentsProvider>
                                      </PickOrganizationsProvider>
                                    </PickGlobalPermissionsProvider>
                                  </PickUsersProvider>
                                </PermissionDataProvider>
                              </BulkSignRevisionModalProvider>
                            </FinishSigningModalProvider>
                          </SignRevisionModalProvider>
                        </DelegateProvider>
                      </PickDeviceTypeProvider>
                    </ConfirmWithMessageProvider>
                  </MessageProvider>
                </ModalsProvider>
              </FileManagerProvider>
            </DepartmentTerminologyProvider>
          </ApiProvider>
        </ResponsiveDesignProvider>
      </MantineProvider>
    </React.StrictMode>
  );
}
